import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { ChevronDown, X, Download, Search } from "react-bootstrap-icons";

const AOVInsightDrawer = ({
    open,
    onClose,
    dateRange = "Last 30 Days",
    groupBy = "Daily",
    selectedDay = "",
    accessToken = localStorage.getItem("authToken"),
}) => {
    const [outletCompareData, setOutletCompareData] = useState([]);
    const [outletCompareLoading, setOutletCompareLoading] = useState(false);
    const [outletCompareError, setOutletCompareError] = useState("");

    const [outletSortBy, setOutletSortBy] = useState("highToLow");
    const [showOutletSortMenu, setShowOutletSortMenu] = useState(false);
    const [outletSearchText, setOutletSearchText] = useState("");

    const [isVisible, setIsVisible] = useState(open);

    const AOV_OUTLET_COMPARISON_API_URL = "http://13.202.138.36:3000/smart-insights/fetch-aov-outlets";

    const groupByMap = {
        Daily: "daily",
        "Day based": "dayBased",
        Weekly: "weekly",
        Monthly: "monthly",
        Quarterly: "quarterly",
        Yearly: "yearly",
    };

    const dateRangeMap = {
        Today: "today",
        Yesterday: "yesterday",
        "Last 7 Days": "last7days",
        "Last 30 Days": "last30days",
        "Last 90 Days": "last90days",
        "Last 3 Months": "last3months",
        "Last 6 Months": "last6months",
        "Last 12 Months": "last12months",
    };

    const formatNumber = (value, decimals = 2) => {
        const num = Number(value || 0);
        return Number(num.toFixed(decimals));
    };

    const normalizeOutletComparison = (items = []) => {
        return items.map((item) => ({
            outletId: item.outletId || item._id,
            outletName: item.outletName || item.name || "Unknown Outlet",
            totalOrders: Number(item.totalOrders || 0),
            totalSales: Number(item.totalSales || 0),
            aov: formatNumber(item.aov || 0),
        }));
    };

    const fetchOutletWiseComparison = useCallback(async () => {
        try {
            setOutletCompareLoading(true);
            setOutletCompareError("");

            const params = {
                filter: dateRangeMap[dateRange] || dateRange || "last30days",
                groupBy: groupByMap[groupBy] || groupBy || "daily",
                selectedDay: groupBy === "Day based" ? selectedDay : "",
                sortBy: outletSortBy,
            };

            const headers = accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {};

            const response = await axios.get(AOV_OUTLET_COMPARISON_API_URL, {
                params,
                headers,
            });

            const payload = response?.data?.data || {};
            setOutletCompareData(normalizeOutletComparison(payload.outlets || []));
        } catch (err) {
            console.error("Outlet wise AOV fetch error:", err);

            setOutletCompareError(
                err?.response?.data?.message ||
                err?.message ||
                "Unable to fetch outlet wise AOV."
            );

            setOutletCompareData([]);
        } finally {
            setOutletCompareLoading(false);
        }
    }, [dateRange, groupBy, selectedDay, outletSortBy, accessToken]);

    useEffect(() => {
        if (!open) return;
        if (groupBy === "Day based" && !selectedDay) return;

        fetchOutletWiseComparison();
    }, [open, groupBy, selectedDay, fetchOutletWiseComparison]);

    useEffect(() => {
        if (open) {
            setIsVisible(true);
        } else {
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 400);

            return () => clearTimeout(timer);
        }
    }, [open]);

    const filteredOutletCompareData = useMemo(() => {
        return outletCompareData.filter((outlet) =>
            outlet.outletName
                .toLowerCase()
                .includes(outletSearchText.toLowerCase())
        );
    }, [outletCompareData, outletSearchText]);

    const maxOutletAov = Math.max(
        ...filteredOutletCompareData.map((item) => Number(item.aov || 0)),
        1
    );

    if (!open && !isVisible) {
        return null;
    }

    return (
        <>
            <div style={drawerOverlayStyle} onClick={onClose}>
                <div
                    style={{
                        ...outletDrawerStyle,
                        animation: open
                            ? "slideInRight 0.32s ease forwards"
                            : "slideOutRight 0.32s ease forwards",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={drawerHeaderStyle}>
                        <div>
                            <h3 style={drawerTitleStyle}>Outlet wise comparison : AOV</h3>
                            <p style={drawerSubTextStyle}>
                                {outletCompareData.length} Outlets
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            style={drawerCloseButtonStyle}
                        >
                            <X size={30} />
                        </button>
                    </div>

                    <div style={drawerFilterBarStyle}>
                        <div style={drawerSearchStyle}>
                            <Search size={16} />
                            <input
                                value={outletSearchText}
                                onChange={(e) => setOutletSearchText(e.target.value)}
                                placeholder="Search outlet"
                                style={drawerSearchInputStyle}
                            />
                        </div>

                        <div style={dropdownWrapStyle}>
                            <button
                                type="button"
                                onClick={() =>
                                    setShowOutletSortMenu((prev) => !prev)
                                }
                                style={drawerDropdownStyle}
                            >
                                {outletSortBy === "highToLow"
                                    ? "High → Low"
                                    : "Low → High"}
                                <ChevronDown size={18} />
                            </button>

                            {showOutletSortMenu && (
                                <div
                                    style={{
                                        ...menuStyle,
                                        right: 0,
                                        left: "auto",
                                        zIndex: 210,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOutletSortBy("highToLow");
                                            setShowOutletSortMenu(false);
                                        }}
                                        style={menuItemStyle}
                                    >
                                        High → Low
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOutletSortBy("lowToHigh");
                                            setShowOutletSortMenu(false);
                                        }}
                                        style={menuItemStyle}
                                    >
                                        Low → High
                                    </button>
                                </div>
                            )}
                        </div>

                        <button type="button" style={drawerDropdownStyle}>
                            {dateRange}
                        </button>
                    </div>

                    {outletCompareError && (
                        <div style={errorBoxStyle}>{outletCompareError}</div>
                    )}

                    {outletCompareLoading ? (
                        <div style={loadingBoxStyle}>Loading outlet wise AOV...</div>
                    ) : filteredOutletCompareData.length === 0 ? (
                        <div style={emptyBoxStyle}>No outlet wise AOV found.</div>
                    ) : (
                        <div style={outletBarsWrapStyle}>
                            {filteredOutletCompareData.map((outlet, index) => {
                                const widthPercent = Math.max(
                                    (Number(outlet.aov || 0) / maxOutletAov) * 100,
                                    Number(outlet.aov || 0) > 0 ? 8 : 4
                                );

                                return (
                                    <div
                                        key={outlet.outletId || index}
                                        style={outletBarRowStyle}
                                    >
                                        <div
                                            style={{
                                                ...outletBarStyle,
                                                width: `${Math.min(widthPercent, 100)}%`,
                                                background:
                                                    outletBarColors[index] || "#9DB8FF",
                                            }}
                                        >
                                            <span style={outletBarLabelStyle}>
                                                {index === 0 ? "👑 " : ""}
                                                {outlet.outletName} &nbsp; ₹
                                                {Math.round(outlet.aov)}
                                            </span>
                                        </div>

                                        <div style={outletMetricPillStyle}>
                                            {outlet.totalOrders} orders
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <style>
                {`
                    @keyframes slideInRight {
                        from {
                            transform: translateX(100%);
                        }
                        to {
                            transform: translateX(0);
                        }
                    }

                    @keyframes slideOutRight {
                        from {
                            transform: translateX(0);
                        }
                        to {
                            transform: translateX(100%);
                        }
                    }

                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }
                `}
            </style>
        </>
    );
};

const dropdownWrapStyle = {
    position: "relative",
};

const menuStyle = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    background: "#fff",
    border: "1px solid #d8dee4",
    borderRadius: 10,
    boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
    minWidth: 180,
    padding: "8px 0",
    zIndex: 50,
};

const menuItemStyle = {
    width: "100%",
    border: 0,
    background: "#fff",
    textAlign: "left",
    padding: "10px 16px",
    fontWeight: 500,
    color: "#17292b",
};

const drawerOverlayStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.52)",
    zIndex: 2000,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "stretch",
    animation: "fadeIn 0.25s ease forwards",
};

const outletDrawerStyle = {
    width: "min(760px, 92vw)",
    height: "100vh",
    background: "#ffffff",
    borderRadius: "22px 0 0 22px",
    padding: 28,
    overflowY: "auto",
    boxShadow: "-18px 0 60px rgba(0,0,0,0.25)",
};

const drawerHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 24,
};

const drawerTitleStyle = {
    fontSize: 23,
    fontWeight: 800,
    color: "#071C1F",
    marginBottom: 6,
};

const drawerSubTextStyle = {
    color: "#687779",
    fontWeight: 700,
    margin: 0,
};

const drawerCloseButtonStyle = {
    border: 0,
    background: "transparent",
    color: "#17292b",
};

const drawerFilterBarStyle = {
    background: "#F8F8F8",
    borderRadius: 14,
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 24,
};

const drawerSearchStyle = {
    height: 46,
    border: "1px solid #D7E0E3",
    background: "#ffffff",
    borderRadius: 10,
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    minWidth: 180,
};

const drawerSearchInputStyle = {
    border: 0,
    outline: 0,
    width: "100%",
    fontWeight: 600,
};

const drawerDropdownStyle = {
    border: "1px solid #D7E0E3",
    background: "#ffffff",
    borderRadius: 10,
    minHeight: 46,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 24,
    fontWeight: 700,
    color: "#17292b",
};

const loadingBoxStyle = {
    background: "#F8F5FF",
    border: "1px solid #E6D8FF",
    color: "#6F3DF4",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 16,
    fontWeight: 700,
};

const errorBoxStyle = {
    background: "#FFF1F2",
    border: "1px solid #FECDD3",
    color: "#E11D48",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 16,
    fontWeight: 700,
};

const emptyBoxStyle = {
    background: "#F8FAFC",
    border: "1px dashed #CBD5E1",
    color: "#64748B",
    borderRadius: 12,
    padding: 18,
    fontWeight: 700,
};

const outletBarsWrapStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    padding: "2px 10px 26px",
};

const outletBarRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minHeight: 45,
};

const outletBarStyle = {
    height: 42,
    borderRadius: 4,
    minWidth: 90,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 14px",
    transition: "width 0.25s ease",
};

const outletBarLabelStyle = {
    color: "#ffffff",
    fontWeight: 800,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

const outletMetricPillStyle = {
    border: "1px solid #DDE5EA",
    borderRadius: 18,
    background: "#ffffff",
    padding: "7px 12px",
    color: "#071C1F",
    fontWeight: 800,
    whiteSpace: "nowrap",
};

const outletBarColors = ["#4944F2", "#5B72F3", "#7994F5", "#9DB8FF"];

export default AOVInsightDrawer;